/**
 * usePublishEvent Hook
 * 
 * Handles the publish flow for events, integrating with react-ui hooks
 * for API configuration and authentication.
 */

import { useState, useCallback } from 'react';
import { useApi, useBalloonEventsMetadata, useUser, BalloonEventNotifier } from 'react-ui';
import { useFormStore } from '../stores/formStore';
import { publishEvent, PublishResult } from '../services/balloonService';
import { getDraftService } from '../services/draftService';
import { clearAllPendingFiles } from '../services/imageService';

export interface UsePublishEventReturn {
  isPublishing: boolean;
  publishError: string | null;
  publishErrorCode: number | null;
  imageUploadErrors: string[];
  publishedEventId: string | null;
  publish: () => Promise<PublishResult>;
  clearError: () => void;
}

export function usePublishEvent(): UsePublishEventReturn {
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishErrorCode, setPublishErrorCode] = useState<number | null>(null);
  const [imageUploadErrors, setImageUploadErrors] = useState<string[]>([]);
  const [publishedEventId, setPublishedEventId] = useState<string | null>(null);

  const { apiEndpoints, apiKey } = useApi();
  const { metadata } = useBalloonEventsMetadata();
  const { user } = useUser();
  const { formData, draftId, resetForm } = useFormStore();

  const clearError = useCallback(() => {
    setPublishError(null);
    setPublishErrorCode(null);
    setImageUploadErrors([]);
  }, []);

  const publish = useCallback(async (): Promise<PublishResult> => {
    // Validate prerequisites
    if (!user?.token) {
      const result: PublishResult = {
        success: false,
        error: 'You must be logged in to publish an event',
      };
      setPublishError(result.error!);
      return result;
    }

    if (!metadata) {
      const result: PublishResult = {
        success: false,
        error: 'Metadata not loaded. Please try again.',
      };
      setPublishError(result.error!);
      return result;
    }

    if (!apiEndpoints || !apiKey) {
      const result: PublishResult = {
        success: false,
        error: 'API configuration not available',
      };
      setPublishError(result.error!);
      return result;
    }

    // Validate required fields
    if (!formData.title || formData.title.length < 3) {
      const result: PublishResult = {
        success: false,
        error: 'Event title is required (minimum 3 characters)',
      };
      setPublishError(result.error!);
      return result;
    }

    if (!formData.categories || formData.categories.length === 0) {
      const result: PublishResult = {
        success: false,
        error: 'At least one category is required',
      };
      setPublishError(result.error!);
      return result;
    }

    if (!formData.dates || formData.dates.length === 0) {
      const result: PublishResult = {
        success: false,
        error: 'At least one date is required',
      };
      setPublishError(result.error!);
      return result;
    }

    if (!formData.location?.coordinates?.lat || !formData.location?.coordinates?.lng) {
      const result: PublishResult = {
        success: false,
        error: 'Event location is required',
      };
      setPublishError(result.error!);
      return result;
    }

    // Clear previous errors and start publishing
    setPublishError(null);
    setPublishErrorCode(null);
    setImageUploadErrors([]);
    setIsPublishing(true);

    try {
      const result = await publishEvent(
        {
          apiEndpoints,
          apiKey,
          metadata,
        },
        formData,
        user.token
      );

      if (result.success) {
        setPublishedEventId(result.eventId || null);
        
        // Set image upload errors if any (event was created but some images failed)
        if (result.imageUploadErrors && result.imageUploadErrors.length > 0) {
          setImageUploadErrors(result.imageUploadErrors);
        }

        // Only clear draft and form on success
        if (draftId && apiKey) {
          try {
            const apiEndpoint = new URL(apiEndpoints.metadata).origin;
            const draftService = getDraftService(apiEndpoint, apiKey);
            await draftService.deleteDraft(user.token, draftId);
          } catch (error) {
            console.warn('Failed to delete draft after publish:', error);
            // Don't fail the publish if draft deletion fails
          }
        }

        // Notify listeners (map, list, detail page) before resetting form
        BalloonEventNotifier.notify({
          id: result.eventId!,
          slug: result.slug,
          lng: result.lng,
          lat: result.lat,
          mode: formData.editId ? 'edit' : formData.isClone ? 'clone' : 'create',
        });

        // Reset form state and clean up pending files on success
        resetForm();
        clearAllPendingFiles();
      } else {
        setPublishError(result.error || 'Failed to publish event');
        setPublishErrorCode(result.statusCode ?? null);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setPublishError(errorMessage);
      setPublishErrorCode(null);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsPublishing(false);
    }
  }, [user, metadata, apiEndpoints, apiKey, formData, draftId, resetForm]);

  return {
    isPublishing,
    publishError,
    publishErrorCode,
    imageUploadErrors,
    publishedEventId,
    publish,
    clearError,
  };
}
